// src/api/paymentsIntegrationApi.ts
//
// RTK Query endpoints for the LevelUp Payments integration (backend PR #252).
// baseUrl is '/api', so paths omit that prefix.
//
// ============================================================================
// WHAT IS DELIBERATELY NOT HERE
// ============================================================================
//
// No finalization endpoint. The browser finalizes DIRECTLY with Payments using
// the short-lived `Provisioning` authority — see `finalizeProvisioning` in
// `lib/paymentsProvisioning.ts`, which calls Payments, not CRM.
//
// No mutation accepts a `paymentToken`, `customer_vault_id`, PaymentMethod id,
// PAN or CVC. CRM's backend has no route that would take one, and adding a
// client for a route that does not exist is how one ends up existing.
import { baseApi } from './baseApi';

/** The five canonical Payments states. CRM invents none of them. */
export type CollectionState =
  | 'ACCEPTED'
  | 'IN_PROGRESS'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'REQUIRES_ATTENTION';

/**
 * What the browser must do next, as SELECTED BY PAYMENTS.
 *
 * This is protocol execution, not processor choice. There is no `useStripe`,
 * no `useNmi`, no `preferredProcessor` — the UI runs whichever protocol it is
 * handed and cannot express a preference.
 */
export type ProvisioningBrowserAction =
  | {
      kind: 'confirm_setup_with_client_secret';
      publicKey: string;
      clientSecret: string;
      /** NULLABLE. Present only where the vendor SDK needs an account scope. */
      providerAccountContext: string | null;
    }
  | { kind: 'tokenize_with_public_key'; publicKey: string };

export interface ProvisioningSession {
  provisioningAuthority: string;
  expiresAt: number;
  browserAction: ProvisioningBrowserAction;
}

export interface PaymentsWorkspaceConfig {
  paymentsMerchantAccountId: string | null;
  configured: boolean;
}

export interface ContactPaymentInstrument {
  id: string;
  paymentInstrumentId: string;
  isDefault: boolean;
  createdAt: string;
}

export interface PaymentCollectionView {
  id: string;
  invoiceId: string;
  invoiceScheduleId: string | null;
  amountMinorAuthorized: string;
  currency: string;
  paymentInstrumentId: string;
  lastKnownState: CollectionState | null;
}

export interface CreateCollectionBody {
  invoiceId: string;
  invoiceScheduleId?: string | null;
  paymentInstrumentId: string;
  /** Minor units as a decimal string. Never a float, never a JSON number. */
  amountMinorAuthorized: string;
  /**
   * ONE key per deliberate user action, reused across every technical retry.
   * See `lib/businessRequestKey.ts` — the backend's UNIQUE
   * (workspaceId, businessRequestKey) is what actually prevents a duplicate
   * economic decision; this field is how the UI participates in that.
   */
  businessRequestKey: string;
}

export const paymentsIntegrationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPaymentsConfig: builder.query<PaymentsWorkspaceConfig, void>({
      query: () => '/payments-integration/config',
      providesTags: [{ type: 'PaymentsConfig', id: 'CURRENT' }],
    }),

    updatePaymentsConfig: builder.mutation<
      PaymentsWorkspaceConfig,
      { paymentsMerchantAccountId: string | null }
    >({
      query: (body) => ({ url: '/payments-integration/config', method: 'PATCH', body }),
      invalidatesTags: [{ type: 'PaymentsConfig', id: 'CURRENT' }],
    }),

    getContactInstruments: builder.query<ContactPaymentInstrument[], { contactId: string }>({
      query: ({ contactId }) => `/payments-integration/contacts/${contactId}/instruments`,
      transformResponse: (r: { instruments: ContactPaymentInstrument[] }) => r.instruments ?? [],
      providesTags: (_result, _err, { contactId }) => [
        { type: 'PaymentInstrument', id: contactId },
      ],
    }),

    // Returns the provisioning authority and the browser action. The authority
    // is a short-lived credential for ONE provisioning; it is not CRM's service
    // token, which never leaves the backend.
    startProvisioningSession: builder.mutation<ProvisioningSession, { contactId: string }>({
      query: ({ contactId }) => ({
        url: `/payments-integration/contacts/${contactId}/instruments/provisioning-sessions`,
        method: 'POST',
      }),
    }),

    // Called AFTER the browser finalized with Payments. Carries a canonical
    // Payments UUID and nothing provider-native.
    associateInstrument: builder.mutation<
      ContactPaymentInstrument,
      { contactId: string; paymentInstrumentId: string; makeDefault?: boolean }
    >({
      query: ({ contactId, ...body }) => ({
        url: `/payments-integration/contacts/${contactId}/instruments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { contactId }) => [{ type: 'PaymentInstrument', id: contactId }],
    }),

    setDefaultInstrument: builder.mutation<
      { id: string; isDefault: boolean },
      { contactId: string; paymentInstrumentId: string }
    >({
      query: ({ contactId, paymentInstrumentId }) => ({
        url: `/payments-integration/contacts/${contactId}/instruments/${paymentInstrumentId}/default`,
        method: 'PATCH',
      }),
      invalidatesTags: (_r, _e, { contactId }) => [{ type: 'PaymentInstrument', id: contactId }],
    }),

    createCollection: builder.mutation<PaymentCollectionView, CreateCollectionBody>({
      query: (body) => ({ url: '/payments-integration/collections', method: 'POST', body }),
    }),

    submitCollection: builder.mutation<
      { collectionExecutionId: string; state: CollectionState },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/payments-integration/collections/${id}/submit`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetPaymentsConfigQuery,
  useUpdatePaymentsConfigMutation,
  useGetContactInstrumentsQuery,
  useStartProvisioningSessionMutation,
  useAssociateInstrumentMutation,
  useSetDefaultInstrumentMutation,
  useCreateCollectionMutation,
  useSubmitCollectionMutation,
} = paymentsIntegrationApi;
